/**
 * 群组筛选条件参数长度限制（t_scrm_scene_event_property.PARAMS varchar(500)）。
 *
 * 这里用 Array.from 按 Unicode code point 计数，与 MySQL varchar 的字符语义一致；
 * 不能用 String.length（UTF-16 单元）或 Buffer.byteLength（UTF-8 字节）。
 */
export const SCENE_PARAMS_MAX_CHARS = 500

export function charLength(value) {
  if (value === null || value === undefined) {
    return 0
  }
  return Array.from(String(value)).length
}

export function joinSelectedCodes(values) {
  if (!Array.isArray(values)) {
    return ''
  }
  return values
    .filter(value => value !== null && value !== undefined && String(value) !== '')
    .map(value => String(value))
    .join(',')
}

export function selectedCodesCharLength(values) {
  return charLength(joinSelectedCodes(values))
}

export function optionCode(option) {
  if (option && typeof option === 'object') {
    return option.dicKey
  }
  return option
}

function normalizeCodes(values) {
  if (!Array.isArray(values)) {
    return []
  }
  return values
    .filter(value => value !== null && value !== undefined && String(value) !== '')
    .map(value => String(value))
}

function normalizeOptionCodes(options) {
  const seen = {}
  const codes = []
  ;(Array.isArray(options) ? options : []).forEach(option => {
    const code = optionCode(option)
    if (code === null || code === undefined || String(code) === '') {
      return
    }
    const key = String(code)
    if (!seen[key]) {
      seen[key] = true
      codes.push(key)
    }
  })
  return codes
}

export function equalOptionCodeLength(options) {
  const codes = normalizeOptionCodes(options)
  if (codes.length === 0) {
    return null
  }
  const length = charLength(codes[0])
  return codes.every(code => charLength(code) === length) ? length : null
}

/**
 * 编码等长时，按实际编码长度计算最多可选数量；编码不等长时返回 null。
 * 拼接后的长度 = N * 编码长度 + (N - 1) 个逗号。
 */
export function maxEqualLengthCount(options, maxChars = SCENE_PARAMS_MAX_CHARS) {
  const length = equalOptionCodeLength(options)
  if (length === null) {
    return null
  }
  if (length > maxChars) {
    return 0
  }
  return Math.floor((maxChars + 1) / (length + 1))
}

/**
 * 已选项永远允许取消；未选项加入后超过上限时禁用。
 */
export function isMultiOptionDisabled(selectedValues, code, maxChars = SCENE_PARAMS_MAX_CHARS) {
  if (code === null || code === undefined || String(code) === '') {
    return true
  }
  const selected = normalizeCodes(selectedValues)
  if (selected.indexOf(String(code)) > -1) {
    return false
  }
  return selectedCodesCharLength(selected.concat([String(code)])) > maxChars
}

export function multiOptionDisabledReason(selectedValues, code, maxChars = SCENE_PARAMS_MAX_CHARS) {
  if (charLength(code) > maxChars) {
    return `单项编码超过 ${maxChars} 字符上限`
  }
  if (selectedCodesCharLength(selectedValues) > maxChars) {
    return `当前已超出 ${maxChars} 字符上限，请先删除部分选项`
  }
  return `加入后超出 ${maxChars} 字符上限`
}

/**
 * 生成业务可读的容量说明，不暴露数据库字段名。
 */
export function describeMultiSelectionLimit(selectedValues, options, maxChars = SCENE_PARAMS_MAX_CHARS) {
  const selected = normalizeCodes(selectedValues)
  const optionCodes = normalizeOptionCodes(options)
  const selectedCount = selected.length
  const joinedLength = selectedCodesCharLength(selected)
  const overLimit = joinedLength > maxChars
  const equalCodeLength = equalOptionCodeLength(optionCodes)
  const capacity = equalCodeLength === null ? null : Math.floor((maxChars + 1) / (equalCodeLength + 1))
  const maxSelectable = capacity === null ? null : Math.min(capacity, optionCodes.length)
  const selectedLengthsMatchOptions = equalCodeLength !== null
    && selected.every(code => charLength(code) === equalCodeLength)

  let hint = ''
  let disabledReason = `加入后超出 ${maxChars} 字符上限`

  if (overLimit) {
    hint = `已选 ${selectedCount} 项，共 ${joinedLength} 字符，已超出 ${maxChars} 字符上限，请先删除部分选项再保存`
    disabledReason = `当前已超出 ${maxChars} 字符上限，请先删除部分选项`
  } else if (selectedCount === 0 && optionCodes.length === 0) {
    hint = ''
  } else if (equalCodeLength !== null && selectedLengthsMatchOptions) {
    if (capacity === 0) {
      hint = `单项编码 ${equalCodeLength} 字符，已超过 ${maxChars} 字符上限，当前选项不可选`
      disabledReason = `单项编码超过 ${maxChars} 字符上限`
    } else {
      hint = `已选 ${selectedCount} 项，最多 ${maxSelectable} 项（每项编码 ${equalCodeLength} 字符，合计不超过 ${maxChars} 字符）`
    }
  } else if (optionCodes.length === 0) {
    hint = `已选 ${selectedCount} 项，共 ${joinedLength}/${maxChars} 字符；当前无可选选项，历史选择已保留`
  } else {
    hint = `已选 ${selectedCount} 项，已用 ${joinedLength}/${maxChars} 字符；编码长度不一致，按实际字符数限制，灰色选项加入后会超出上限`
  }

  return {
    selectedCount,
    joinedLength,
    overLimit,
    maxChars,
    equalCodeLength,
    capacity,
    maxSelectable,
    hint,
    disabledReason
  }
}

function appendParamsError(errors, rule, meta, maxChars) {
  const length = charLength(rule && rule.params)
  if (length > maxChars) {
    errors.push({
      scope: meta.scope,
      index: meta.index,
      eventIndex: meta.eventIndex,
      ruleIndex: meta.ruleIndex,
      label: meta.label,
      length,
      maxChars
    })
  }
}

/**
 * 校验真正要提交的 payload，而不是页面上的临时选择状态。
 * 用户属性与用户行为的筛选条件落库时都写入 scene_event_property.PARAMS。
 */
export function collectParamsLimitErrors(payload, maxChars = SCENE_PARAMS_MAX_CHARS) {
  const errors = []
  const userFilters = (payload && payload.userEvent && payload.userEvent.filtersRuleList) || []
  userFilters.forEach((rule, index) => {
    appendParamsError(errors, rule, {
      scope: 'userProperty',
      index,
      label: `用户属性第${index + 1}个条件`
    }, maxChars)
  })

  const eventRules = (payload && payload.userGroupRule && payload.userGroupRule.ruleList) || []
  eventRules.forEach((eventRule, eventIndex) => {
    const filters = (eventRule && eventRule.filtersRuleList) || []
    filters.forEach((rule, ruleIndex) => {
      appendParamsError(errors, rule, {
        scope: 'userAction',
        eventIndex,
        ruleIndex,
        label: `用户行为第${eventIndex + 1}个事件第${ruleIndex + 1}个条件`
      }, maxChars)
    })
  })
  return errors
}

export function formatParamsLimitError(error) {
  if (!error) {
    return ''
  }
  return `${error.label}的已选内容共${error.length}个字符，超过${error.maxChars}个字符上限，请减少选择项后保存`
}
